import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    Chip,
    Grid,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Pagination,
} from "@mui/material";

import { EditOutlined, DeleteOutline } from "@mui/icons-material";

import {
    collection,
    getDocs,
    doc,
    setDoc,
    deleteDoc,
    query,
    where,
    Timestamp,
} from "firebase/firestore";

import {
    createUserWithEmailAndPassword,
    getAuth,
} from "firebase/auth";

import { db } from "../../firebase";
import Swal from "sweetalert2";

/* ================= CONSTANTS ================= */

const PAGE_SIZE = 5;

const ROLE_OPTIONS = [
    "Admin",
    "Manager",
];

function Usersmgmt() {
    const navigate = useNavigate();
    const auth = getAuth();

    const [users, setUsers] = useState([]);
    const [editingId, setEditingId] = useState(null);

    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);

    const [form, setForm] = useState({
        name: "",
        email: "",
        mobile: "",
        password: "",
        role: "Admin",
        status: "Active",
    });


    /* ================= LOAD USERS ================= */

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        const q = query(collection(db, "users"));
        const snap = await getDocs(q);
        const arr = snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
        }));
        setUsers(arr);
    };

    /* ================= FORM HANDLER ================= */

    const handleChange = (e) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value,
        });
    };

    /* ================= SAVE USER ================= */

    const saveUser = async () => {
        try {
            let uid = editingId;

            if (!editingId) {
                const userCredential = await createUserWithEmailAndPassword(
                    auth,
                    form.email,
                    form.password
                );
                uid = userCredential.user.uid;

                auth.signOut();
            }

            await setDoc(doc(db, "users", uid), {
                uid,
                ...form,
                createdTime: editingId ? form.createdTime : Timestamp.now(),
            });

            Swal.fire("Success", editingId ? "User updated" : "User added", "success");

            setForm({
                name: "",
                email: "",
                mobile: "",
                password: "",
                role: "Admin",
                status: "Active",
            });

            setEditingId(null);
            loadUsers();
        } catch (error) {
            Swal.fire("Error", error.message, "error");
        }
    };

    /* ================= EDIT USER ================= */

    const editUser = (u) => {
        setForm(u);
        setEditingId(u.uid);
    };

    /* ================= DELETE USER ================= */

    const deleteUser = async (uid) => {
        const res = await Swal.fire({
            title: "Delete user?",
            icon: "warning",
            showCancelButton: true,
        });

        if (!res.isConfirmed) return;

        await deleteDoc(doc(db, "users", uid));
        Swal.fire("Deleted", "User removed", "success");
        loadUsers();
    };

    /* ================= FILTER & PAGINATION ================= */

    const filtered = users.filter(
        (u) =>
            u.name?.toLowerCase().includes(search.toLowerCase()) ||
            u.email?.toLowerCase().includes(search.toLowerCase())
    );

    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <Box display="flex" minHeight="100vh" bgcolor="#f4f6f8">
            <Box flex={1}>
                <Typography variant="h5" fontWeight={800} mb={2}>
                    Users Mgmt
                </Typography>

                {/* ================= FORM ================= */}
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography fontWeight={700} mb={2}>
                            {editingId ? "Edit User" : "Add New User"}
                        </Typography>

                        <Grid className="row" spacing={2}>
                            <Grid className="col-sm-3 mb-3">
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Name"
                                    name="name"
                                    value={form.name}
                                    onChange={handleChange}
                                />
                            </Grid>

                            <Grid className="col-sm-3 mb-3">
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Email"
                                    name="email"
                                    value={form.email}
                                    onChange={handleChange}
                                />
                            </Grid>

                            <Grid className="col-sm-3 mb-3">
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Mobile"
                                    name="mobile"
                                    value={form.mobile}
                                    onChange={handleChange}
                                />
                            </Grid>

                            {!editingId && (
                                <Grid className="col-sm-3 mb-3">
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Password"
                                        name="password"
                                        value={form.password}
                                        onChange={handleChange}
                                    />
                                </Grid>
                            )}

                            <Grid className="col-sm-3 mb-3">
                                <FormControl fullWidth size="small">
                                    <InputLabel>Role</InputLabel>
                                    <Select name="role" value={form.role} onChange={handleChange}>
                                        {ROLE_OPTIONS.map((r) => (
                                            <MenuItem key={r} value={r}>
                                                {r}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid className="col-sm-3 mb-3">
                                <Button variant="contained" onClick={saveUser}>
                                    {editingId ? "Update User" : "Add User"}
                                </Button>
                            </Grid>
                        </Grid>


                    </CardContent>
                </Card>

                {/* ================= SEARCH ================= */}
                <TextField
                    label="Search by name or email"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ mb: 2 }} size="small"
                />

                {/* ================= TABLE ================= */}
                <Card>
                    <CardContent>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Sr No</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Mobile</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Role</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {paginated.map((u, index) => (
                                    <TableRow key={u.uid}>
                                        <TableCell>{(page - 1) * PAGE_SIZE + index + 1}</TableCell>
                                        <TableCell>{u.name}</TableCell>
                                        <TableCell>{u.mobile}</TableCell>
                                        <TableCell>{u.email}</TableCell>
                                        <TableCell>{u.role}</TableCell>
                                       
                                        <TableCell align="center">
                                            <IconButton color="primary" onClick={() => editUser(u)}>
                                                <EditOutlined />
                                            </IconButton>
                                            <IconButton color="error" onClick={() => deleteUser(u.uid)}>
                                                <DeleteOutline />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        <Box display="flex" justifyContent="center" mt={2}>
                            <Pagination
                                count={Math.ceil(filtered.length / PAGE_SIZE)}
                                page={page}
                                onChange={(e, v) => setPage(v)}
                            />
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
}

export default Usersmgmt;